import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import styled from 'styled-components'
import { rem } from 'polished'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import TierProgressionInfoModal from './TierProgressionInfoModal'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { getTierOrder } from 'loyalty/lib/tierUtils'

const DesktopContainer = styled.div`
  position: relative;
  padding-top: ${rem(14)};
`

const PillWrapper = styled.button`
  position: absolute;
  top: ${rem(-2)};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`

const DesktopPanel = styled.div`
  padding: ${rem(20)} ${rem(16)} ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

interface Props {
  className?: string
  /** The lowest tier that any package can unlock (e.g., "silver" for cheapest package) */
  lowestUnlockableTier: App.LoyaltyTier
  /** The lowest tier name that any package can unlock (e.g., "Silver" for cheapest package) */
  lowestUnlockableTierName: string
  /** The highest tier that any package can reach (e.g., Platinum for most expensive) */
  highestUnlockableTier: App.LoyaltyTier
  highestUnlockableTierName: string
  /** True if lowest !== highest (show "fast-track" messaging) */
  hasMultipleTierJumps?: boolean
  /** Status credits needed to reach next tier (for original message) */
  statusCreditsNeeded: number
  /** Nights needed to reach next tier (for original message) */
  nightsNeeded: number
  /** Status review date (for original message) */
  statusReviewDate?: string
  checkoutDate?: string
}

function TierProgressionBanner({
  className,
  lowestUnlockableTier,
  lowestUnlockableTierName,
  highestUnlockableTier,
  highestUnlockableTierName,
  hasMultipleTierJumps = false,
  statusCreditsNeeded,
  nightsNeeded,
  statusReviewDate,
  checkoutDate,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const showCredits = statusCreditsNeeded > 0
  const showNights = nightsNeeded > 0

  const tiersToShow = useMemo(() => {
    const tierOrder = getTierOrder()
    const lowestIndex = tierOrder.indexOf(lowestUnlockableTier)
    const highestIndex = tierOrder.indexOf(highestUnlockableTier)
    return tierOrder.slice(lowestIndex, highestIndex + 1)
  }, [lowestUnlockableTier, highestUnlockableTier])

  const handleOpenModal = useCallback<MouseEventHandler>((event) => {
    event.preventDefault()
    showModal(<TierProgressionInfoModal tiers={tiersToShow} checkoutDate={checkoutDate} />)
  }, [showModal, tiersToShow, checkoutDate])

  if (!hasMultipleTierJumps && !showCredits && !showNights) {
    return null
  }

  const pillText = hasMultipleTierJumps
    ? intl.formatMessage({
        id: '63pNz0szlBa0W-ZWY-9i',
        defaultMessage: 'Fast-track to {tierName}',
        description: 'Pill text when multiple tier jumps are available in the loyalty program section',
      }, { tierName: highestUnlockableTierName })
    : intl.formatMessage({
        id: 'ONP4KJXwd7UmxJQciCQX',
        defaultMessage: 'Unlock {tierName}',
        description: 'Pill text to unlock a single tier in the loyalty program section',
      }, { tierName: highestUnlockableTierName })

  const renderSingleTierMessage = () => {
    const hasDate = !!statusReviewDate
    if (showCredits && showNights && hasDate) {
      return (
        <FormattedMessage
          id="dEVyKB-VFMMZHmVE7Iqo"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and <b>{nights} Nights</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Tier progression message with credits, nights, and review date"
          values={{
            statusCredits: statusCreditsNeeded,
            nights: nightsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    }
    if (showCredits && showNights) {
      return (
        <FormattedMessage
          id="r4-IGQIdCqxvuZSz99he"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and <b>{nights} Nights</b> to unlock {tierName} status."
          description="Tier progression message with credits and nights in the loyalty program section"
          values={{
            statusCredits: statusCreditsNeeded,
            nights: nightsNeeded,
            tierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    }
    if (showCredits && hasDate) {
      return (
        <FormattedMessage
          id="EvyQKi7YK57tFr0H0h43"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Tier progression message with credits and review date"
          values={{
            statusCredits: statusCreditsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    }
    if (showCredits) {
      return (
        <FormattedMessage
          id="UV-VIhodD8Ms3DPjW9OF"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> to unlock {tierName} status."
          description="Tier progression message with credits only in the loyalty section"
          values={{
            statusCredits: statusCreditsNeeded,
            tierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    }
    if (showNights && hasDate) {
      return (
        <FormattedMessage
          id="JhG0_t44PALSFuDTuoO6"
          defaultMessage="Book a package worth <b>{nights} Nights</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Tier progression message with nights and review date"
          values={{
            nights: nightsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    }
    // showNights only
    return (
      <FormattedMessage
        id="bBNW8kMBwh1cq6MCipTW"
        defaultMessage="Book a package worth <b>{nights} Nights</b> to unlock {tierName} status."
        description="Tier progression message with nights only in the loyalty section"
        values={{
          nights: nightsNeeded,
          tierName: highestUnlockableTierName,
          b: (chunks) => <b>{chunks}</b>,
        }}
      />
    )
  }

  const bodyText = hasMultipleTierJumps
    ? (
        <FormattedMessage
          id="BHiHcdZenhA3dSpy-zap"
          defaultMessage="This offer includes packages that unlock <b>{lowestTierName}</b> or fast-track you straight to <b>{highestTierName}</b> status."
          description="Message when offer has packages unlocking multiple tier levels"
          values={{
            lowestTierName: lowestUnlockableTierName,
            highestTierName: highestUnlockableTierName,
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      )
    : renderSingleTierMessage()

  return (
    <DesktopContainer className={className}>
      <PillWrapper onClick={handleOpenModal}>
        <LoyaltyTierPill tier={highestUnlockableTier} kind="secondary" endIcon={<LineInfoCircleIcon />}>
          {pillText}
        </LoyaltyTierPill>
      </PillWrapper>
      <DesktopPanel>
        <BodyText variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="dtOaeochuxCBNbrQqw2P"
            defaultMessage="{bodyText} <link>Learn more</link>"
            description="Tier progression body text with link to open info modal"
            values={{
              bodyText,
              link: (chunks) => <TextLink onClick={handleOpenModal}>{chunks}</TextLink>,
            }}
          />
        </BodyText>
      </DesktopPanel>
    </DesktopContainer>
  )
}

export default TierProgressionBanner
